import { useChatSession } from '../../hooks/useChatSession';
import { ChatComposer } from './ChatComposer';
import { ChatTranscript } from './ChatTranscript';

interface ChatPanelProps {
  /** Existing session to load and continue; absent = fresh session on first send. */
  sessionId?: string;
  /** Fires when the first send creates a session (navigate / remember it). */
  onSessionCreated?: (sessionId: string) => void;
  autoFocus?: boolean;
  /** Idle hint under the composer when the transcript is empty. */
  idleHint?: string;
}

const DEFAULT_IDLE_HINT =
  'I answer from your journal, lists, and notes — and file what you tell me, ' +
  'asking your approval before I save anything.';

/**
 * One chat conversation: transcript + approval cards + composer. The "one
 * panel, two hosts" surface (docs/frontend.md): the /alma page and the global
 * capture sheet BOTH render this component — one state machine
 * (useChatSession), one stream path, never a second copy.
 */
export function ChatPanel({ sessionId, onSessionCreated, autoFocus, idleHint }: ChatPanelProps) {
  const {
    turns,
    busy,
    phase,
    statusLabel,
    streamed,
    errorMessage,
    errorDetail,
    loadingSession,
    sessionMissing,
    send,
    respond,
  } = useChatSession({ sessionId, onSessionCreated });

  if (sessionMissing) {
    return (
      <section className="glass-panel shadow-warm rounded-2xl p-5 sm:p-6" aria-label="Conversation">
        <p className="text-sm text-[var(--text-secondary)]">
          This conversation is gone — it may have been deleted.
        </p>
      </section>
    );
  }

  return (
    <div className="space-y-4">
      {(turns.length > 0 || loadingSession || errorMessage) && (
        <section
          aria-label="Conversation"
          className="glass-panel shadow-warm rounded-2xl p-5 sm:p-6 animate-fade-in-up"
        >
          {loadingSession ? (
            <div className="space-y-3" aria-hidden>
              <div className="skeleton h-4 w-2/3 rounded" />
              <div className="skeleton h-4 w-1/2 rounded" />
              <div className="skeleton h-4 w-3/5 rounded" />
            </div>
          ) : (
            <>
              <ChatTranscript
                turns={turns}
                streamed={streamed}
                statusLabel={statusLabel}
                busy={busy}
                onRespond={(approve) => void respond(approve)}
                deciding={phase === 'deciding'}
              />
              {errorMessage && (
                <div
                  role="alert"
                  className="mt-4 bg-[color-mix(in_oklab,var(--coral-400)_10%,transparent)] border border-[color-mix(in_oklab,var(--coral-400)_35%,transparent)] text-[var(--text-primary)] px-4 py-3 rounded-xl"
                >
                  <p>{errorMessage}</p>
                  {errorDetail && (
                    <p className="mt-1.5 text-xs font-mono text-[var(--text-tertiary)] break-words">
                      {errorDetail}
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </section>
      )}

      <section className="glass-panel shadow-warm rounded-2xl p-5 sm:p-6" aria-label="Tell me">
        <ChatComposer onSend={send} disabled={busy || loadingSession} autoFocus={autoFocus} />
        {turns.length === 0 && !loadingSession && (
          <p className="mt-3 text-sm text-[var(--text-tertiary)]">
            {idleHint ?? DEFAULT_IDLE_HINT}
          </p>
        )}
      </section>
    </div>
  );
}
